import { cn } from "@core-ui/utils/cn";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";
import { cloneElement, type KeyboardEvent, type MouseEvent, type ReactElement, type ReactNode } from "react";

interface DisabledReasonProps {
	/** Why the action is unavailable. Shown on hover and on keyboard focus. */
	reason: ReactNode;
	/** Off means the child is rendered untouched, without a tooltip. */
	disabled?: boolean;
	/** For text fields: `aria-disabled` conveys the state but does not stop typing, `readOnly` does. */
	readOnly?: boolean;
	/**
	 * The action itself. It must forward its ref -- the tooltip anchors to it -- and merge the `className` it
	 * receives into its own instead of letting a later spread overwrite it.
	 */
	children: ReactElement;
}

// A control styled through `disabled:` variants loses that styling together with the native
// attribute, so the same look is restored on the ARIA state. Pointer events stay on -- the tooltip
// is the whole point.
const DISABLED_LOOK = "aria-disabled:opacity-50 aria-disabled:cursor-not-allowed";

const swallow = (event: MouseEvent | KeyboardEvent) => {
	event.preventDefault();
	event.stopPropagation();
};

/**
 * An action that cannot be taken right now, with the reason attached to it.
 *
 * The child keeps `aria-disabled` instead of `disabled`: a natively disabled control drops out of
 * the tab order, so its explanation is reachable by mouse only -- keyboard users and screen readers
 * never get it. Here the control stays focusable and inert, and the tooltip opens on hover and on
 * focus alike.
 */
export const DisabledReason = ({ reason, disabled = true, readOnly, children }: DisabledReasonProps) => {
	if (!disabled || !reason) return children;

	const className = cn(children.props.className, DISABLED_LOOK);

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				{cloneElement(children, {
					"aria-disabled": true,
					disabled: undefined,
					readOnly: readOnly || undefined,
					className,
					onClick: swallow,
					onKeyDown: (event: KeyboardEvent) => {
						if (event.key === "Enter" || event.key === " ") swallow(event);
					},
				} as Partial<typeof children.props>)}
			</TooltipTrigger>
			<TooltipContent>{reason}</TooltipContent>
		</Tooltip>
	);
};
